/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import { observer } from "mobx-react";
import { observable, action } from "mobx";
import DialerServiceData from "Stores/DialerServiceStore";
import userDetail from "Stores/userDetail";
import { toasterState } from "Components/common/toaster";

// ---------------------------------------------------------------------------
// Modal state (singleton, same pattern as BuyNewNumberPopup)
// ---------------------------------------------------------------------------

class VoBizKycModalState {
  @observable showPopup = false;

  @action
  setShowPopup = val => {
    this.showPopup = val;
    if (val) {
      document.getElementsByTagName("body")[0].classList.add("scrollHidden");
    } else {
      document.getElementsByTagName("body")[0].classList.remove("scrollHidden");
    }
  };
}

const voBizKycModalState = new VoBizKycModalState();

// ---------------------------------------------------------------------------
// Document type options
// ---------------------------------------------------------------------------

const DOC_TYPES = [
  { value: "AADHAAR",          label: "Aadhaar Card" },
  { value: "PAN",              label: "PAN Card" },
  { value: "PASSPORT",         label: "Passport" },
  { value: "VOTER_ID",         label: "Voter ID" },
  { value: "DRIVING_LICENSE",  label: "Driving License" }
];

// Documents that require a back-side upload
const REQUIRES_BACK = ["AADHAAR", "DRIVING_LICENSE"];

// Status badge config
const STATUS_CONFIG = {
  PENDING:   { color: "#f59e0b", label: "Pending Upload" },
  SUBMITTED: { color: "#3b82f6", label: "Under Review" },
  VERIFIED:  { color: "#10b981", label: "Verified ✓" },
  REJECTED:  { color: "#ef4444", label: "Rejected" }
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const VoBizKycModal = observer(() => {
  const teamId = userDetail?.userInfo?.team_id || userDetail?.userFeatureFlag?.id || "";

  const [step, setStep] = useState("status"); // "status" | "form"
  const [kycData, setKycData] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);

  // Form state
  const [docType, setDocType] = useState("AADHAAR");
  const [docNumber, setDocNumber] = useState("");
  const [applicantName, setApplicantName] = useState("");
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const frontInputRef = useRef(null);
  const backInputRef = useRef(null);

  // Load KYC status on open
  useEffect(() => {
    if (voBizKycModalState.showPopup && teamId) {
      loadKycStatus();
    }
  }, [voBizKycModalState.showPopup]);

  const loadKycStatus = async () => {
    setLoadingStatus(true);
    const data = await DialerServiceData.getVoBizKycStatus();
    setKycData(data);
    setLoadingStatus(false);
    // If no KYC record yet, show form directly
    if (!data || data.statusCode === "NOT_FOUND") {
      setStep("form");
    } else {
      setStep("status");
    }
  };

  const closeModal = () => {
    resetForm();
    voBizKycModalState.setShowPopup(false);
  };

  const resetForm = () => {
    setDocType("AADHAAR");
    setDocNumber("");
    setApplicantName("");
    setFrontFile(null);
    setBackFile(null);
    setFormErrors({});
    setStep("status");
  };

  const validate = () => {
    const errors = {};
    if (!docNumber.trim()) errors.docNumber = "Document number is required";
    if (!applicantName.trim()) errors.applicantName = "Full name is required";
    if (!frontFile) errors.frontFile = "Front side document is required";
    if (REQUIRES_BACK.includes(docType) && !backFile)
      errors.backFile = "Back side document is required for " + docType;
    return errors;
  };

  const handleSubmit = async e => {
    e && e.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSubmitting(true);
    const result = await DialerServiceData.submitVoBizKyc(
      docType,
      docNumber.trim(),
      applicantName.trim(),
      frontFile,
      REQUIRES_BACK.includes(docType) ? backFile : null
    );
    setSubmitting(false);

    if (result) {
      setKycData(result);
      setStep("status");
    }
  };

  const handleFileChange = (e, side) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    // Max 5 MB
    if (file.size > 5 * 1024 * 1024) {
      toasterState.setToastMsg("File size must be under 5 MB", "failure");
      return;
    }
    if (side === "front") {
      setFrontFile(file);
      setFormErrors(prev => ({ ...prev, frontFile: "" }));
    } else {
      setBackFile(file);
      setFormErrors(prev => ({ ...prev, backFile: "" }));
    }
  };

  if (!voBizKycModalState.showPopup) return null;

  return (
    <div className="buyNewNumberPopup popupWrapper">
      <div className="confirmation-popup common-popup">
        <div
          className="newNumberPopupWrapper popup-wrap animated customzoomIn"
          style={{ maxWidth: "520px", width: "100%" }}
        >
          <div className="newNumberCont">

            {/* Header */}
            <div className="popupHeader">
              <div className="popupTitle">KYC Verification</div>
              <div className="popup-close" title="Close" onClick={closeModal}>
                <i className="material-icons">close</i>
              </div>
            </div>

            {/* Body */}
            <div className="popupBody" style={{ padding: "16px 24px" }}>

              {loadingStatus ? (
                <div style={{ textAlign: "center", padding: "24px 0", color: "#888" }}>
                  Loading KYC status...
                </div>
              ) : step === "status" && kycData && kycData.statusCode === "SUCCESS" ? (
                <StatusView
                  kycData={kycData}
                  onResubmit={() => setStep("form")}
                />
              ) : (
                <KycForm
                  docType={docType}
                  setDocType={setDocType}
                  docNumber={docNumber}
                  setDocNumber={setDocNumber}
                  applicantName={applicantName}
                  setApplicantName={setApplicantName}
                  frontFile={frontFile}
                  backFile={backFile}
                  frontInputRef={frontInputRef}
                  backInputRef={backInputRef}
                  handleFileChange={handleFileChange}
                  formErrors={formErrors}
                  submitting={submitting}
                  onSubmit={handleSubmit}
                  onCancel={closeModal}
                />
              )}

            </div>
          </div>
        </div>
      </div>
      <div className="common-popup-mask" />
    </div>
  );
});

// ---------------------------------------------------------------------------
// Status view — shown when KYC record exists
// ---------------------------------------------------------------------------

const StatusView = ({ kycData, onResubmit }) => {
  const {
    kycStatus = "PENDING",
    documentType = "",
    documentNumber = "",
    applicantName = "",
    rejectionReason = "",
    submittedAt,
    verifiedAt
  } = kycData || {};

  const cfg = STATUS_CONFIG[kycStatus] || STATUS_CONFIG.PENDING;

  return (
    <div style={{ padding: "8px 0" }}>
      {/* Status badge */}
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          backgroundColor: cfg.color + "22",
          border: `1px solid ${cfg.color}`,
          borderRadius: "6px",
          padding: "6px 14px",
          marginBottom: "16px"
        }}
      >
        <span
          style={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            backgroundColor: cfg.color,
            display: "inline-block"
          }}
        />
        <span style={{ color: cfg.color, fontWeight: 600, fontSize: "13px" }}>
          {cfg.label}
        </span>
      </div>

      {/* Details */}
      <div style={{ display: "grid", gridTemplateColumns: "130px 1fr", gap: "8px 12px", fontSize: "13px", marginBottom: "16px" }}>
        {documentType && (
          <>
            <span style={{ color: "#888" }}>Document Type</span>
            <span>{DOC_TYPES.find(d => d.value === documentType)?.label || documentType}</span>
          </>
        )}
        {documentNumber && (
          <>
            <span style={{ color: "#888" }}>Document No.</span>
            <span>{documentNumber}</span>
          </>
        )}
        {applicantName && (
          <>
            <span style={{ color: "#888" }}>Applicant Name</span>
            <span>{applicantName}</span>
          </>
        )}
        {submittedAt && (
          <>
            <span style={{ color: "#888" }}>Submitted At</span>
            <span>{new Date(submittedAt).toLocaleDateString()}</span>
          </>
        )}
        {verifiedAt && (
          <>
            <span style={{ color: "#888" }}>Verified At</span>
            <span>{new Date(verifiedAt).toLocaleDateString()}</span>
          </>
        )}
      </div>

      {/* Rejection reason */}
      {kycStatus === "REJECTED" && rejectionReason && (
        <div
          style={{
            backgroundColor: "#fef2f2",
            border: "1px solid #fca5a5",
            borderRadius: "6px",
            padding: "10px 14px",
            fontSize: "13px",
            color: "#b91c1c",
            marginBottom: "16px"
          }}
        >
          <strong>Rejection reason: </strong>{rejectionReason}
        </div>
      )}

      {/* Resubmit for rejected */}
      {kycStatus === "REJECTED" && (
        <div className="btnCont" style={{ display: "flex", justifyContent: "flex-end" }}>
          <div
            className="purchaseBtn btn"
            style={{ padding: "8px 20px", fontSize: "13px", cursor: "pointer" }}
            onClick={onResubmit}
          >
            Resubmit KYC
          </div>
        </div>
      )}

      {kycStatus === "VERIFIED" && (
        <div style={{ fontSize: "13px", color: "#10b981" }}>
          Your KYC is verified. You can now purchase India numbers.
        </div>
      )}

      {kycStatus === "SUBMITTED" && (
        <div style={{ fontSize: "13px", color: "#3b82f6" }}>
          Your documents are under review. This typically takes 1-2 business days.
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// KYC form
// ---------------------------------------------------------------------------

const KycForm = ({
  docType, setDocType,
  docNumber, setDocNumber,
  applicantName, setApplicantName,
  frontFile, backFile,
  frontInputRef, backInputRef,
  handleFileChange,
  formErrors,
  submitting,
  onSubmit, onCancel
}) => {
  const needsBack = REQUIRES_BACK.includes(docType);

  return (
    <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>

      <p style={{ fontSize: "13px", color: "#555", margin: 0 }}>
        Indian TRAI regulations require identity verification before activating India DIDs.
        Please upload a valid government-issued document.
      </p>

      {/* Document type */}
      <div>
        <label style={labelStyle}>Document Type <span style={{ color: "red" }}>*</span></label>
        <select
          value={docType}
          onChange={e => setDocType(e.target.value)}
          style={selectStyle}
        >
          {DOC_TYPES.map(dt => (
            <option key={dt.value} value={dt.value}>{dt.label}</option>
          ))}
        </select>
      </div>

      {/* Document number */}
      <div>
        <label style={labelStyle}>Document Number <span style={{ color: "red" }}>*</span></label>
        <input
          type="text"
          placeholder="Enter the ID number on the document"
          value={docNumber}
          onChange={e => setDocNumber(e.target.value)}
          style={{ ...inputStyle, borderColor: formErrors.docNumber ? "#ef4444" : "#d1d5db" }}
        />
        {formErrors.docNumber && <div style={errStyle}>{formErrors.docNumber}</div>}
      </div>

      {/* Applicant name */}
      <div>
        <label style={labelStyle}>Full Name (as on document) <span style={{ color: "red" }}>*</span></label>
        <input
          type="text"
          placeholder="Enter full name"
          value={applicantName}
          onChange={e => setApplicantName(e.target.value)}
          style={{ ...inputStyle, borderColor: formErrors.applicantName ? "#ef4444" : "#d1d5db" }}
        />
        {formErrors.applicantName && <div style={errStyle}>{formErrors.applicantName}</div>}
      </div>

      {/* Front side upload */}
      <div>
        <label style={labelStyle}>
          {needsBack ? "Front Side" : "Document"} (JPG / PNG / PDF, max 5 MB){" "}
          <span style={{ color: "red" }}>*</span>
        </label>
        <div
          style={uploadBoxStyle(formErrors.frontFile)}
          onClick={() => frontInputRef.current && frontInputRef.current.click()}
        >
          {frontFile ? (
            <span style={{ color: "#374151", fontSize: "13px" }}>{frontFile.name}</span>
          ) : (
            <span style={{ color: "#9ca3af", fontSize: "13px" }}>
              <i className="material-icons" style={{ fontSize: "18px", verticalAlign: "middle", marginRight: "4px" }}>upload_file</i>
              Click to upload
            </span>
          )}
          <input
            ref={frontInputRef}
            type="file"
            accept="image/jpeg,image/png,application/pdf"
            style={{ display: "none" }}
            onChange={e => handleFileChange(e, "front")}
          />
        </div>
        {formErrors.frontFile && <div style={errStyle}>{formErrors.frontFile}</div>}
      </div>

      {/* Back side upload (conditional) */}
      {needsBack && (
        <div>
          <label style={labelStyle}>
            Back Side (JPG / PNG / PDF, max 5 MB) <span style={{ color: "red" }}>*</span>
          </label>
          <div
            style={uploadBoxStyle(formErrors.backFile)}
            onClick={() => backInputRef.current && backInputRef.current.click()}
          >
            {backFile ? (
              <span style={{ color: "#374151", fontSize: "13px" }}>{backFile.name}</span>
            ) : (
              <span style={{ color: "#9ca3af", fontSize: "13px" }}>
                <i className="material-icons" style={{ fontSize: "18px", verticalAlign: "middle", marginRight: "4px" }}>upload_file</i>
                Click to upload
              </span>
            )}
            <input
              ref={backInputRef}
              type="file"
              accept="image/jpeg,image/png,application/pdf"
              style={{ display: "none" }}
              onChange={e => handleFileChange(e, "back")}
            />
          </div>
          {formErrors.backFile && <div style={errStyle}>{formErrors.backFile}</div>}
        </div>
      )}

      {/* Actions */}
      <div className="actionBtn" style={{ paddingTop: "4px" }}>
        <div className="btnCont">
          <div className="backCont btn" onClick={onCancel}>Cancel</div>
          <button
            type="submit"
            className={`purchaseBtn btn ${submitting ? "disabled" : ""}`}
            disabled={submitting}
            style={{ border: "none", cursor: submitting ? "not-allowed" : "pointer" }}
          >
            {submitting ? "Submitting..." : "Submit KYC"}
          </button>
        </div>
      </div>
    </form>
  );
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const labelStyle = {
  display: "block",
  fontSize: "12px",
  fontWeight: 600,
  color: "#374151",
  marginBottom: "4px"
};

const inputStyle = {
  width: "100%",
  padding: "8px 10px",
  fontSize: "13px",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  outline: "none",
  boxSizing: "border-box"
};

const selectStyle = {
  ...inputStyle,
  backgroundColor: "#fff",
  cursor: "pointer"
};

const uploadBoxStyle = hasError => ({
  border: `1px dashed ${hasError ? "#ef4444" : "#d1d5db"}`,
  borderRadius: "6px",
  padding: "12px 16px",
  cursor: "pointer",
  backgroundColor: "#f9fafb",
  display: "flex",
  alignItems: "center",
  minHeight: "44px"
});

const errStyle = {
  fontSize: "11px",
  color: "#ef4444",
  marginTop: "3px"
};

export default VoBizKycModal;
export { VoBizKycModal, voBizKycModalState };
